/**
 * 문항 카드 AI 액션 runner — 가드 → 잠금·스켈레톤 → 요청 → 리비전 가드 → 적용 → 계측.
 * 액션별 차이는 전부 QUESTION_CARD_AI_ACTION_SPECS 에 있고, 이 훅은 액션과 무관하게 한 경로로 돈다.
 * 폼(react-hook-form)이 진실이다. index 는 적용 시점에 id 로 다시 찾는다(findQuiz).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtom, useStore } from 'jotai';
import { nanoid } from 'nanoid';
import { useTranslation } from 'next-i18next';

import '../utils/events.quiz-question-card-ai';

import { trackEvent } from '@zep/logger/tracking';

import {
  usePremiumAccess,
  useUpsellingGuard,
  useUserPlanManager,
} from '@/shared/plan';
import { alertDialog } from '@/ui_v3';

import { EditTools } from '../../../components/EditableQuizCard/EditTools';
import { useQuizList } from '../../../components/useQuizList';
import { useBuildFormContext } from '../../../form/schema';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { useBuildQuestionAlert } from '../../../hook/useBuildQuestionAlert';
import { aiSaveAnimation } from '../../../util/aiSaveAnimation';
// barrel 이 아니라 파일 경로 — barrel 은 채팅 utils 까지 끌어와 순환 참조가 열린다.
import { openAiBuilderAuthExpiredDialog } from '../../ai-builder-chat/components/AiBuilderAuthExpiredDialog';
import { useAiBuilderBuilderContext } from '../../ai-builder-chat/hooks/useAiBuilderBuilderContext';
import { QUESTION_CARD_AI_TIMEOUT_MS } from '../consts/questionCardAi.const';
import {
  dropSlot,
  putSlot,
  type QuestionCardAiSlot,
  runningQuestionCardAiActionAtom,
} from '../stores/questionCardAi.atoms';
import {
  type ActionTarget,
  type Discarded,
  type Failure,
  type FormWriter,
  type RunnableQuestionCardAiAction,
} from '../types/questionCardAi.types';
import {
  type QuestionCardAiMenuPlace,
  type QuestionCardAiTrackedPlace,
} from '../utils/events.quiz-question-card-ai';
import {
  QUESTION_CARD_AI_ACTION_SPECS,
  type QuestionCardAiActionSpec,
} from '../utils/questionCardAiActionSpecs';
import { cancelAbortReason, cancelByOf } from '../utils/questionCardAiCancel';
import { questionCardAiErrorMessage } from '../utils/questionCardAiErrorMessage';
import {
  resolveApplyGuard,
  terminalOutcome,
  waitAtLeast,
} from '../utils/questionCardAiOutcome';
import {
  type QuestionCardAiFailureRoute,
  redirectToLoginAndReturn,
  resolveFailureRoute,
} from '../utils/questionCardAiRecovery';
import {
  fillSlotVariantOf,
  slotIdsOf,
} from '../utils/questionCardAiSlotSchema';
import { toSourceQuestion } from '../utils/toSourceQuestion';

import { createQuestionCardAiCopy } from './useQuestionCardAiCopy';

const timeoutError = () =>
  new DOMException('question card AI timed out', 'TimeoutError');

export const useQuestionCardAiActions = ({ quizId }: { quizId: string }) => {
  const { getValues, setValue } = useBuildFormContext();
  const { handleInsert } = useQuizList();
  const { openQuestionCountLimitAlert } = useBuildQuestionAlert();
  const builderContext = useAiBuilderBuilderContext();
  const upsellingGuard = useUpsellingGuard();
  const { userPlanManager } = useUserPlanManager();
  const { openFeaturePaywall } = usePremiumAccess();
  const { t, i18n } = useTranslation(['build']);
  const chatTool = EditTools.useAiGenerateTool();

  // 슬롯 atom 은 스펙 값이라 useSetAtom 을 정적으로 둘 수 없다 — store 로 동적으로 쓴다.
  const store = useStore();
  const [running, setRunning] = useAtom(runningQuestionCardAiActionAtom);
  // 이 훅 인스턴스가 시작한 요청만 소유한다. 카드가 사라지거나 build 화면을
  // 벗어나면 중단해, 전역 잠금이 최대 60초간 남는 상태를 막는다.
  const ownedControllerRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => ownedControllerRef.current?.abort(cancelAbortReason('unmount')),
    [],
  );

  // 적용 시점 비교는 **최신** context 로 해야 한다. 요청 때 캡처한 값을 양쪽에
  // 쓰면 학년·과목 변경이 구조적으로 감지되지 않는다.
  const builderContextRef = useRef({
    schoolLevel: builderContext.schoolLevel,
    grade: builderContext.grade,
    subject: builderContext.subject,
  });
  builderContextRef.current = {
    schoolLevel: builderContext.schoolLevel,
    grade: builderContext.grade,
    subject: builderContext.subject,
  };

  /**
   * id 로 문항을 다시 찾는다. 부를 때마다 최신 폼을 읽으므로 index 는 "지금" 자리다.
   * 반환값을 보관하지 말 것 — 요청 전과 응답 후에 각각 새로 부른다.
   */
  const findQuiz = useCallback((): ActionTarget | null => {
    const list = getValues('quizList');
    const index = list.findIndex(quiz => quiz.id === quizId);
    return index === -1
      ? null
      : { quiz: list[index], index, count: list.length };
  }, [getValues, quizId]);

  const writer = useMemo<FormWriter>(
    () => ({
      setValue,
      insertAfter: (index, items) => handleInsert(index + 1, items),
      openQuizLimitAlert: openQuestionCountLimitAlert,
      highlight: aiSaveAnimation,
    }),
    [handleInsert, openQuestionCountLimitAlert, setValue],
  );

  /** 이 카드의 슬롯만 닫는다 — 다른 카드에 남은 실패 UI 를 지우면 안 된다. */
  const closeOwnSlot = useCallback(
    (spec: QuestionCardAiActionSpec, onlyIf?: QuestionCardAiSlot['status']) =>
      store.set(spec.slotAtom, prev => dropSlot(prev, quizId, onlyIf)),
    [quizId, store],
  );

  const execute = useCallback(
    async (
      action: RunnableQuestionCardAiAction,
      place: QuestionCardAiTrackedPlace,
    ) => {
      const spec = QUESTION_CARD_AI_ACTION_SPECS[action];

      // ① 요청 전 조회 — quiz 만 쓴다. found.index 는 아무도 읽지 않는다.
      const found = findQuiz();
      if (!found) return;
      // 자격 재검사 — retry 는 메뉴를 안 거친다. 실행하면 안 되면 이 액션의 슬롯만 닫는다.
      if (!spec.eligibility(found).enabled) {
        closeOwnSlot(spec);
        return;
      }

      // 비교 기준(어떤 슬롯을 볼지)은 지금 굳힌다 — found.quiz 는 live 객체라 나중엔 오염된다.
      // 값과 builderContext 는 호출 시점(요청 전·적용 직전) 최신을 읽는다.
      const revision = spec.revisionFor(found.quiz);
      const revisionOf = (quiz: BuildQuizListItem) =>
        revision(quiz, builderContextRef.current);
      const requestedRevision = revisionOf(found.quiz);

      const controller = new AbortController();
      ownedControllerRef.current = controller;
      // AbortSignal.any 대신 타이머 하나로 취소·타임아웃을 같은 경로에 모은다.
      const timeoutTimer = setTimeout(
        () => controller.abort(timeoutError()),
        QUESTION_CARD_AI_TIMEOUT_MS,
      );

      const startedAt = Date.now();
      const quizType = found.quiz.type;

      setRunning({ quizId, action, controller, startedAt });
      store.set(spec.slotAtom, prev =>
        putSlot(prev, {
          quizId,
          status: 'running',
          action,
          slotIds: slotIdsOf(found.quiz),
        }),
      );
      trackEvent('quiz_question_card_ai_action_start', {
        action,
        quiz_type: quizType,
        place,
      });

      let model = '';
      let appliedCount = 0;
      // 조기 폐기(카드 삭제·문항 상한)도 terminal 이벤트를 남겨야 퍼널이 안 깨진다.
      let discarded: Discarded = null;
      let failure: Failure = null;
      try {
        const result = await spec.run(
          {
            locale: i18n.language,
            question: toSourceQuestion(found.quiz),
            builderContext: builderContextRef.current,
            clientRequestId: nanoid(),
            signal: controller.signal,
          },
          found.quiz,
        );
        model = result.model;
        if (spec.minVisibleMs != null && spec.minVisibleMs !== 0)
          await waitAtLeast(startedAt, spec.minVisibleMs);
        // 대기 중 취소됐다면 적용하지 않는다 — 대기는 signal 을 받지 않아 끝까지 돈다.
        controller.signal.throwIfAborted();

        // ② 응답 후 재조회 — 이 target 만 apply 로 들어간다. quiz 와 index 가 같은 스냅샷이다.
        const now = findQuiz();
        discarded = resolveApplyGuard(now, requestedRevision, revisionOf);
        if (!discarded && now) {
          closeOwnSlot(spec); // 스켈레톤 자리를 비운 뒤 적용
          ({ discarded, appliedCount } = result.apply(now, writer));
        }
      } catch (error) {
        const { code, route } = resolveFailureRoute(error, spec.failure);
        if (route !== 'slot') closeOwnSlot(spec);
        const presentFailure: Record<QuestionCardAiFailureRoute, () => void> = {
          cancelled: () => undefined,
          slot: () =>
            store.set(spec.slotAtom, prev =>
              putSlot(prev, { quizId, status: 'error', code, action }),
            ),
          alert: () =>
            alertDialog.open({
              header: {
                title: questionCardAiErrorMessage(
                  createQuestionCardAiCopy(t),
                  code,
                  fillSlotVariantOf(quizType),
                ),
              },
            }),
          // 재시도로 풀리지 않는 실패는 인라인 오류 대신 복구 흐름으로 보낸다.
          auth_expired: () =>
            openAiBuilderAuthExpiredDialog({
              onConfirm: redirectToLoginAndReturn,
            }),
          plan_denied: () =>
            void openFeaturePaywall({
              requiredPlan: userPlanManager.getFeaturePlanType(spec.feature),
              from: `question_card_${action}`,
            }),
        };
        presentFailure[route]();
        failure = { cancelled: route === 'cancelled', code };
      } finally {
        if (ownedControllerRef.current === controller)
          ownedControllerRef.current = null;
        // terminal 이벤트는 여기 한 곳에서만 낸다 — 조기 return 경로가
        // click 만 남기고 result 를 빠뜨리면 퍼널·성공률이 왜곡된다.
        const { result, errorCode, discardReason } = terminalOutcome(
          failure,
          discarded,
        );
        trackEvent('quiz_question_card_ai_action_end', {
          action,
          quiz_type: quizType,
          place,
          result,
          duration_ms: Date.now() - startedAt,
          error_code: errorCode,
          discard_reason: discardReason,
          cancel_by: result === 'cancel' ? cancelByOf(controller.signal) : '',
          applied_count: result === 'success' ? appliedCount : 0,
          model,
        });
        clearTimeout(timeoutTimer);
        setRunning(null);
        // 조용히 폐기된 경로(카드 삭제·상한)에서도 running 슬롯이 남지 않게 정리한다.
        closeOwnSlot(spec, 'running');
      }
    },
    [
      closeOwnSlot,
      findQuiz,
      i18n.language,
      openFeaturePaywall,
      t,
      quizId,
      setRunning,
      store,
      userPlanManager,
      writer,
    ],
  );

  // 클릭은 플랜 게이트보다 먼저 남긴다 — 게이트에서 막힌 시도도 퍼널 분모에 들어가야 한다.
  const run = useCallback(
    (
      action: RunnableQuestionCardAiAction,
      place: QuestionCardAiTrackedPlace,
    ) => {
      if (running) return;
      trackEvent('quiz_question_card_ai_action_click', {
        action,
        quiz_type: findQuiz()?.quiz.type ?? '',
        place,
      });
      upsellingGuard(
        () => {
          void execute(action, place);
        },
        {
          feature: QUESTION_CARD_AI_ACTION_SPECS[action].feature,
          from: `question_card_${action}`,
        },
      );
    },
    [execute, findQuiz, running, upsellingGuard],
  );

  const cancel = useCallback(() => {
    if (running?.quizId === quizId)
      running.controller.abort(cancelAbortReason('user'));
  }, [quizId, running]);

  // 액션을 인자로 받는다. 두 슬롯이 동시에 error 일 때 어느 쪽 "다시 시도" 인지
  // 호출부만 알기 때문에, 여기서 추측하면 사용자가 누르지 않은 생성이 돈다.
  const retry = useCallback(
    (action: RunnableQuestionCardAiAction) => {
      if (running) return;
      run(action, 'failure_card');
    },
    [run, running],
  );

  const openChat = useMemo(() => {
    if (!chatTool) return null;
    return (place: QuestionCardAiMenuPlace) => {
      trackEvent('quiz_question_card_ai_action_click', {
        action: 'chat',
        quiz_type: findQuiz()?.quiz.type ?? '',
        place,
      });
      chatTool.onClick();
    };
  }, [chatTool, findQuiz]);

  return {
    run,
    cancel,
    retry,
    openChat,
    isLocked: running !== null,
    runningAction: running?.quizId === quizId ? running.action : null,
  };
};
